/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Empty from "antd/lib/empty";
import { retrieveApi } from "api/retrive";
import { Mission } from "api/retrive/type";
import { ReactComponent as MissionSvg } from "assets/temporary/icons/mission.svg";
import Loading from "components/Common/Loading";
import HeaderCustom from "components/Mobile/ui/HeaderCustom";
import { useAppSelector } from "hooks/app-hook";
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { theme } from "styles/theme";

const { typography: typo, palette } = theme;

const ChallengeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 1200px;
  padding: 20px;
  background-color: ${theme.colors.black100};
  border-radius: 12px;
  border: 1px solid ${theme.borders.primary};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  color: ${palette.text.primary};

  svg,
  svg path {
    fill: ${palette.text.accent};
  }

  span {
    font-size: ${typo.sectionTitle.fontSize};
    font-weight: ${typo.sectionTitle.fontWeight};
    line-height: ${typo.sectionTitle.lineHeight};
    letter-spacing: ${typo.sectionTitle.letterSpacing};
    color: ${palette.text.primary};
  }
`;

const MissionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MissionCard = styled.div`
  padding: 16px 20px;
  background: rgba(10, 8, 11, 0.75);
  backdrop-filter: blur(8px);
  border: 1px solid ${palette.border.default};
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${palette.border.strong};
    background: rgba(20, 16, 10, 0.85);
  }
`;

const MissionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px dashed ${palette.border.subtle};

  .name {
    font-size: ${typo.cardTitle.fontSize};
    font-weight: ${typo.cardTitle.fontWeight};
    line-height: ${typo.cardTitle.lineHeight};
    color: ${palette.text.primary};
  }

  .reward {
    font-size: ${typo.bodySm.fontSize};
    font-weight: 700;
    line-height: ${typo.bodySm.lineHeight};
    color: ${palette.text.accent};
    padding: 4px 10px;
    border-radius: 999px;
    background: ${palette.accent.tint};
    border: 1px solid ${palette.border.default};
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: ${typo.body.fontSize};
  font-weight: ${typo.body.fontWeight};
  line-height: ${typo.body.lineHeight};
  margin-bottom: 6px;

  .label {
    color: ${palette.text.secondary};
    font-size: ${typo.bodySm.fontSize};
  }
  .value {
    color: ${palette.text.primary};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const EmptyCard = styled.div`
  padding: 40px 20px;
  background: rgba(10, 8, 11, 0.75);
  backdrop-filter: blur(8px);
  border: 1px solid ${palette.border.default};
  border-radius: 12px;
  color: ${palette.text.primary};

  .ant-empty-description {
    color: ${palette.text.primary} !important;
    font-size: ${typo.body.fontSize};
    font-weight: 500;
  }
  .ant-empty-image {
    opacity: 0.65;
  }
`;

interface StatusProps {
  status: string;
}

const statusColor = (status: string): string => {
  switch (status) {
    case "Completed":
      return palette.text.success;
    case "Failed":
      return palette.text.danger;
    case "Entitlement":
      return palette.text.info;
    default:
      return palette.text.muted;
  }
};

const Status = styled.span<StatusProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${typo.bodySm.fontSize};
  font-weight: 600;
  line-height: ${typo.bodySm.lineHeight};
  color: ${({ status }) => statusColor(status)};

  &::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ status }) => statusColor(status)};
  }
`;

const Challenge = () => {
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(false);
  const username = useAppSelector((state) => state.auth.username);
  const { t } = useTranslation("challenge");

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        setLoading(true);
        const response = await retrieveApi.getPromotionChanllenge(username);
        if (response.data) {
          setMissions(response.data);
        }
      } catch (error) {
        console.error("Error fetching challenges:", error);
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchChallenges();
    }
  }, [username]);

  return (
    <>
      {isMobile && <HeaderCustom title={t("title")} />}
      <ChallengeWrapper>
        {!isMobile && (
          <Header>
            <MissionSvg width={22} height={22} />
            <span>{t("title")}</span>
          </Header>
        )}
        {loading ? (
          <Loading height={500} width={"100%"} center />
        ) : missions.length === 0 ? (
          <EmptyCard>
            <Empty description={t("message:Empty")} />
          </EmptyCard>
        ) : (
          <MissionList>
            {missions.map((m, idx) => (
              <MissionCard key={idx}>
                <MissionHeader>
                  <span className="name">{m.promotion_name}</span>
                  <span className="reward">{m.reward_percentage}</span>
                </MissionHeader>
                <InfoRow>
                  <span className="label">{t("TargetTurnover")}</span>
                  <span className="value">{m.achieve_amount}</span>
                </InfoRow>
                <InfoRow>
                  <span className="label">{t("currentAmount")}</span>
                  <span className="value">{m.current_amount}</span>
                </InfoRow>
                <div
                  css={css`
                    margin-top: 12px;
                    padding-top: 12px;
                    border-top: 1px dashed ${palette.border.subtle};
                  `}
                >
                  <Status status={m.status_pro}>{m.status_pro}</Status>
                </div>
              </MissionCard>
            ))}
          </MissionList>
        )}
      </ChallengeWrapper>
    </>
  );
};

export default Challenge;
